import { useEffect, useMemo, useState } from 'react'
import {
  formatDashboardDate,
  formatEffortMinutes,
} from '@/components/Gestores/gestorDashboardFormat'
import {
  GestorDashboardTablePagination,
  paginateRows,
} from '@/components/Gestores/gestorDashboardTimeTablePagination'
import { GestorDashboardTaskLink } from '@/components/Gestores/GestorDashboardTaskIdChip'
import type { GestorDashboardTimeEntry } from '@/components/Gestores/gestorDashboardTypes'
import { GestorListTableShell } from '@/components/Gestores/GestorListTableShell'

type Props = {
  entries: GestorDashboardTimeEntry[]
  loading?: boolean
  onTaskOpen?: (taskId: number) => void
}

export function GestorDashboardTimeTable({ entries, loading = false, onTaskOpen }: Props) {
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [entries])

  const pagination = useMemo(() => paginateRows(entries, page), [entries, page])

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between gap-2'>
        <div>
          <h3 className='text-sm font-semibold text-text-primary'>Apontamentos eKyte</h3>
          <p className='text-xs text-text-muted'>
            {entries.length} registros
            {pagination.totalPages > 1 ? ` · página ${pagination.page} de ${pagination.totalPages}` : ''}
          </p>
        </div>
      </div>
      <GestorListTableShell
        loading={loading}
        empty={!loading && entries.length === 0}
        emptyTitle='Nenhum apontamento encontrado para os filtros selecionados.'
      >
        <table className='min-w-[1200px] w-full text-left text-sm'>
          <thead>
            <tr className='border-b border-[var(--glass-border)] text-xs uppercase tracking-wide text-text-muted'>
              <th className='px-3 py-2'>Data</th>
              <th className='px-3 py-2'>Realizado</th>
              <th className='px-3 py-2'>Executor</th>
              <th className='px-3 py-2'>Cliente</th>
              <th className='px-3 py-2'>Tipo</th>
              <th className='px-3 py-2'>Tarefa</th>
              <th className='px-3 py-2'>Etapa</th>
              <th className='px-3 py-2'>Comentário</th>
              <th className='px-3 py-2'>Ticket</th>
              <th className='px-3 py-2'>Campanha</th>
              <th className='px-3 py-2'>Produtivo</th>
            </tr>
          </thead>
          <tbody>
            {pagination.rows.map((entry) => (
              <tr key={entry.id} className='border-b border-[var(--glass-border)] hover:bg-white/[0.02]'>
                <td className='whitespace-nowrap px-3 py-2'>{formatDashboardDate(entry.date)}</td>
                <td className='whitespace-nowrap px-3 py-2 tabular-nums'>{formatEffortMinutes(entry.effortMinutes)}</td>
                <td className='px-3 py-2'>{entry.executor ?? '—'}</td>
                <td className='px-3 py-2'>{entry.workspace ?? '—'}</td>
                <td className='px-3 py-2'>{entry.taskType ?? '—'}</td>
                <td className='max-w-[280px] px-3 py-2'>
                  {entry.taskId && onTaskOpen ? (
                    <GestorDashboardTaskLink taskId={entry.taskId} label={entry.task ?? '—'} onOpen={onTaskOpen} />
                  ) : (
                    <span className='block max-w-[220px] truncate' title={entry.task ?? undefined}>
                      {entry.task ?? '—'}
                    </span>
                  )}
                </td>
                <td className='px-3 py-2'>{entry.phase ?? '—'}</td>
                <td className='max-w-[220px] truncate px-3 py-2' title={entry.titleOrComment ?? undefined}>
                  {entry.titleOrComment ?? '—'}
                </td>
                <td className='max-w-[180px] truncate px-3 py-2' title={entry.ticket ?? undefined}>
                  {entry.ticket ?? '—'}
                </td>
                <td className='max-w-[160px] truncate px-3 py-2' title={entry.campaign ?? undefined}>
                  {entry.campaign ?? '—'}
                </td>
                <td className='px-3 py-2'>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      entry.productive
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-white/[0.06] text-text-muted'
                    }`}
                  >
                    {entry.productive ? 'Sim' : 'Não'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <GestorDashboardTablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          from={pagination.from}
          to={pagination.to}
          total={pagination.total}
          onPageChange={setPage}
        />
      </GestorListTableShell>
    </div>
  )
}
